import React, { useState, useEffect, useCallback } from "react";
import { useGetFootprintArticlesQuery, useGetFootprintArticleQuery } from "../../../store/slices/api";
import BlockRenderer from "./BlockRenderer";
import Zoomable from "./Zoomable";
import { cloudinaryThumb, cloudinaryFull, cloudinaryStage } from "./cloudinary";
import { formatDate, groupArticles, groupByYear, orderPlaces, bylineFor } from "./util";
import useScrollFade from "./useScrollFade";
import ScrollCue from "./ScrollCue";

/**
 * A section read as a magazine: an index at the top -- each group's first piece
 * on a stage, the rest as a quiet list -- then every piece laid out in full, then
 * a Contents page. Anything in the index or the contents jumps to its spread.
 *
 * This replaces grid → click → separate reader page. That older shape made you
 * leave the section to read anything and come back to find your place, and it
 * hid how much is actually in here.
 *
 * Each spread fetches its own body only when it approaches the viewport, so the
 * index appears immediately even though Family Stories is 166 blocks in total.
 */

function anchorFor(slug) {
  return `a-${String(slug || "").replace(/[^a-z0-9-]/gi, "")}`;
}

// The part a piece plays in its series -- "Entry No. 2", "Letter No. 3" -- which
// is all a list row needs to say once the series name is the heading above it.
const PART_LABEL = /(?:Letter|Entry|Part|Chapter)\s*(?:No\.?\s*)?\d+\s*$/i;
function partLabel(title) {
  const m = PART_LABEL.exec(title || "");
  return m ? m[0].trim() : title;
}

// Every part of a series usually shares ONE cover, so a picture on every row
// repeats the same image down the list. Sam, 2026-09-11: hide only the repeats --
// a row keeps its picture when it is a different one. Returns a check that says
// "show" only the first time an image appears within a group.
//
// A series goes further (Sam, 2026-09-11): its parts are versions of one cover,
// sometimes saved as different files that still look alike, so series rows carry
// no picture at all -- only the featured part does.
function firstSightings(alreadyShown = []) {
  const seen = new Set(alreadyShown.filter(Boolean));
  return (url) => {
    if (!url || seen.has(url)) return false;
    seen.add(url);
    return true;
  };
}

const PLAQUE =
  "bg-[#fcfaf4] dark:bg-neutral-800 border border-blairGold/25 dark:border-white/10";

// The first piece of a group, on a stage: the whole cover, never cropped or
// zoomed (Sam, 2026-09-11: the zoomed version "looks a little odd").
function StageCard({ article, n, isSeries }) {
  const cover = article.cover_image_url ? cloudinaryStage(article.cover_image_url) : null;
  return (
    <a
      data-stage-card
      href={`#${anchorFor(article.slug)}`}
      className={`block no-underline rounded-[8px] p-[12px] pb-[15px] ${PLAQUE}
                  shadow-[0_18px_34px_-24px_rgba(32,43,75,0.42)]
                  hover:-translate-y-[2px] transition-transform duration-300 ease-out
                  motion-reduce:transition-none motion-reduce:hover:translate-y-0`}
    >
      <div className="h-[230px] flex items-center justify-center overflow-hidden rounded-[4px]
                      bg-[#efe9dc] dark:bg-neutral-700">
        {cover && (
          <img src={cover} alt="" loading="lazy" className="block max-w-full max-h-[230px] w-auto h-auto" />
        )}
      </div>
      <div className="pt-[11px] px-[4px]">
        <div className="font-blairBody font-bold text-[12px] tracking-[0.16em] text-blairGoldDk dark:text-blairGold">
          {String(n).padStart(2, "0")}
        </div>
        <div className="font-blairHead text-[16px] leading-[1.35] text-blairNavy dark:text-darkText mt-[3px]">
          {article.title}
        </div>
        <div className="font-blairBody font-semibold text-[13px] text-blairGoldDk dark:text-blairGold mt-[8px]">
          {isSeries ? "Start with part 1 →" : "Read →"}
        </div>
      </div>
    </a>
  );
}

function ListRow({ article, n, isSeries, showThumb }) {
  return (
    <a
      data-list-row
      href={`#${anchorFor(article.slug)}`}
      className={`flex items-center gap-[12px] no-underline rounded-[6px] p-[8px] ${PLAQUE}
                  hover:border-blairGold/60 transition-colors`}
    >
      {showThumb && (
        <img
          src={cloudinaryThumb(article.cover_image_url)}
          alt=""
          loading="lazy"
          className="w-[84px] aspect-[3/2] object-cover rounded-[3px] flex-none"
        />
      )}
      <div className="min-w-0 py-[2px]">
        <div className="font-blairBody font-bold text-[12px] tracking-[0.16em] text-blairGoldDk dark:text-blairGold">
          {String(n).padStart(2, "0")}
        </div>
        <div className="font-blairHead text-[14px] leading-[1.3] text-blairNavy dark:text-darkText line-clamp-2">
          {isSeries ? partLabel(article.title) : article.title}
        </div>
      </div>
    </a>
  );
}

// One group of the index: its first piece on a stage, the rest as a quiet list
// beside it (Sam, 2026-09-11, "style B") -- far less to take in than a wall of
// tiles, and plain where to start. A group of one shows just its stage, centred.
function StageBand({ band, numberOf, flip = false }) {
  const [lead, ...rest] = band.articles;
  const show = firstSightings([lead.cover_image_url]);

  // A PAIR gets two equal cards, not a stage and one lonely row. With only two
  // pieces the old shape put a full-size picture beside a single thin line and
  // left half the band empty (Sam, 2026-09-17: "make sure that these are the
  // same size... just maek sure this si balanced"). The layout follows the
  // NUMBER of entries: one is centred, two are equal, three or more earn a
  // stage with a list beside it.
  if (band.articles.length === 2) {
    return (
      <div className="max-w-[860px] mx-auto grid grid-cols-1 sm:grid-cols-2 gap-[20px] items-start">
        {band.articles.map((a, i) => (
          // Only the FIRST card may say "Start with part 1" — the second links
          // to part 2, and telling a reader to start there is simply false.
          //
          // A pair alternates too (Sam, 2026-09-17: "these need to alternate
          // sides as the descend"). Its lead card swaps to the right when the
          // group is flipped, so no two groups running down the page start on
          // the same side. Only the WIDE layout reorders — stacked on a phone
          // part 1 always comes first.
          <div key={a.id} className="min-w-0">
            <StageCard article={a} n={numberOf.get(a.slug)} isSeries={Boolean(band.isSeries) && i === 0} />
          </div>
        ))}
      </div>
    );
  }
  // The stage swaps sides group by group (Sam, 2026-09-17: the index was "left
  // heavy" with every big picture down one edge and dead space down the other).
  // Only the WIDE layout alternates: stacked on a phone the picture always
  // comes first, because reading order is not decoration.
  const columns = flip
    ? "md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]"
    : "md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]";
  return (
    <div
      className={
        rest.length
          ? `max-w-[860px] mx-auto grid grid-cols-1 ${columns} gap-[20px] items-start`
          : "max-w-[440px] mx-auto"
      }
    >
      <div className={flip ? "md:order-2 min-w-0" : "min-w-0"}>
        <StageCard article={lead} n={numberOf.get(lead.slug)} isSeries={Boolean(band.isSeries)} />
      </div>
      {rest.length > 0 && (
        <div className={"flex flex-col gap-[10px] min-w-0 " + (flip ? "md:order-1" : "")}>
          {rest.map((a) => (
            <ListRow
              key={a.id}
              article={a}
              n={numberOf.get(a.slug)}
              isSeries={Boolean(band.isSeries)}
              showThumb={!band.isSeries && show(a.cover_image_url)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// A contents page at the end of the section (Sam, 2026-09-11), so a reader who
// has scrolled to the bottom can jump anywhere without climbing back up. Same
// picture rules as the index: none in a series; elsewhere an image shows the
// first time it appears in a group.
function Contents({ bands, numberOf }) {
  return (
    <div className="px-[24px] pt-[70px] pb-[20px]">
      <nav
        aria-label="Contents"
        className={`max-w-[620px] mx-auto rounded-[10px] px-[28px] pt-[24px] pb-[16px] ${PLAQUE}
                    shadow-[0_20px_40px_-26px_rgba(32,43,75,0.45)]`}
      >
        <div className="text-center font-blairHead text-[13px] tracking-[0.3em] uppercase
                        text-blairNavy dark:text-darkText mb-[6px]">
          Contents
        </div>
        {bands.map((b) => {
          const show = firstSightings();
          return (
            <div key={b.name || "all"} className="mt-[14px]">
              {b.name && (
                <div className="font-blairBody font-semibold text-[12px] tracking-[0.16em] uppercase
                                text-blairGoldDk dark:text-blairGold border-b border-blairGold/25 pb-[6px] mb-[4px]">
                  {b.name}
                </div>
              )}
              {b.articles.map((a) => {
                const thumb = !b.isSeries && show(a.cover_image_url);
                return (
                  <a
                    key={a.id}
                    href={`#${anchorFor(a.slug)}`}
                    className="flex items-center gap-[12px] py-[6px] px-[2px] no-underline rounded-[4px] hover:bg-blairGold/10"
                  >
                    <span className="w-[54px] flex-none">
                      {thumb && (
                        <img
                          src={cloudinaryThumb(a.cover_image_url)}
                          alt=""
                          loading="lazy"
                          className="w-[54px] aspect-[3/2] object-cover rounded-[2px]"
                        />
                      )}
                    </span>
                    <span className="w-[24px] flex-none font-blairBody font-semibold text-[12px] text-blairGoldDk dark:text-blairGold">
                      {String(numberOf.get(a.slug)).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 truncate font-blairHead text-[15px] text-blairNavy dark:text-darkText">
                      {b.isSeries ? partLabel(a.title) : a.title}
                    </span>
                    <span className="flex-1 min-w-[16px] border-b border-dotted border-blairNavy/25 mb-[4px] mx-[4px]" aria-hidden="true" />
                    <span className="text-blairGoldDk dark:text-blairGold" aria-hidden="true">→</span>
                  </a>
                );
              })}
            </div>
          );
        })}
      </nav>
    </div>
  );
}


// Announces a new category as you scroll into it. Without this, the move from
// "BOOM Goes the Brandywine" into "E.I. In Love" looked exactly like the move
// from one letter to the next — the same thin rule for both — so the sets ran
// together and the reader lost the shape of the section.
function CategoryBreak({ label, count }) {
  // Gold on cream fails contrast at small sizes, and stacking a gold kicker over
  // a navy title with the rules between them read as three separate things. One
  // line, navy on cream, gold reserved for the rule and the count.
  const isYear = /^\d{4}$/.test(label);
  return (
    <div className="w-full px-[24px] pt-[64px] pb-[6px]">
      <div className="max-w-[880px] mx-auto flex items-center gap-[20px]">
        <span className="flex-1 h-px bg-blairNavy/20" aria-hidden="true" />
        <span className="flex items-baseline gap-[11px] whitespace-nowrap">
          <span
            className={
              "font-blairHead uppercase text-blairNavy dark:text-darkText " +
              (isYear
                ? "text-[21px] sm:text-[25px] tracking-[0.2em]"
                : "text-[16px] sm:text-[19px] tracking-[0.15em]")
            }
          >
            {label}
          </span>
          {count != null && (
            <span className="font-blairBody font-semibold text-[12px] tracking-[0.16em]
                             uppercase text-blairGoldDk dark:text-blairGold">
              {count} {count === 1 ? "part" : "parts"}
            </span>
          )}
        </span>
        <span className="flex-1 h-px bg-blairNavy/20" aria-hidden="true" />
      </div>
      <div className="mx-auto mt-[13px] h-[2px] w-[42px] bg-blairGold" aria-hidden="true" />
    </div>
  );
}

function Spread({ article, n, opensBand }) {
  // One observer does both jobs: the scroll-linked fade, and telling us when
  // this spread has come near enough to be worth fetching its body.
  const [fadeRef, opacity, approached] = useScrollFade();
  const {
    data: full,
    isError: bodyFailed,
    isFetching: bodyFetching,
    refetch: retryBody,
  } = useGetFootprintArticleQuery(article.slug, { skip: !approached });
  const blocks = full?.blocks || [];

  return (
    <section
      id={anchorFor(article.slug)}
      className={
        "scroll-mt-[16px] px-[24px] " +
        // A category break already separates these; a rule as well reads as two
        // dividers stacked.
        (opensBand ? "" : "border-t-[3px] border-white dark:border-neutral-700")
      }
    >
      {/* Generously spaced so each piece gets its own screen rather than
          butting against the next. */}
      <div
        ref={fadeRef}
        style={{ opacity }}
        className="py-[74px] sm:py-[104px] transition-opacity duration-[900ms] ease-out motion-reduce:!opacity-100"
      >
      <div className="text-center mb-[22px] max-w-[860px] mx-auto">
        <div className="font-blairBody font-bold text-[12px] tracking-[0.3em] text-blairGold mb-[10px]">
          {String(n).padStart(2, "0")}
        </div>
        <h2 className="font-blairHead text-[19px] sm:text-[25px] leading-[1.22] tracking-[0.05em]
                       uppercase text-blairNavy dark:text-darkText max-w-[760px] mx-auto">
          {article.title}
        </h2>
        <div className="mx-auto mt-[14px] h-[3px] w-[52px] bg-blairGold" aria-hidden="true" />
        <p className="font-blairBody text-[13px] text-gray-500 dark:text-gray-400 mt-[13px]">
          {formatDate(article.original_published_at || article.created_at)}
          {bylineFor(article.author) ? ` · ${bylineFor(article.author)}` : ""}
        </p>
      </div>

      {article.cover_image_url && (
        <div className="max-w-[560px] mx-auto mb-[30px]">
          <Zoomable images={[cloudinaryFull(article.cover_image_url)]} label={article.title}>
            <img
              src={cloudinaryFull(article.cover_image_url)}
              alt={article.title}
              loading="lazy"
              className="block w-full max-h-[260px] object-contain"
            />
          </Zoomable>
        </div>
      )}

      {blocks.length > 0 ? (
        <BlockRenderer blocks={blocks} />
      ) : bodyFailed && !bodyFetching ? (
        // Never "Loading…" forever: a body that failed says so, with a retry.
        <div className="text-center font-blairBody py-[26px]">
          <p className="text-[14px] text-gray-500 dark:text-gray-400">Couldn’t load this piece.</p>
          <RetryButton onClick={retryBody} />
        </div>
      ) : full ? null : (
        <p className="text-center font-blairBody text-[13px] text-gray-400 py-[26px]">Loading…</p>
      )}
      </div>
    </section>
  );
}

// One group in the index, and whether it is folded. State lives here rather
// than in the page so opening one group never re-renders the others.
function IndexGroup({ band, numberOf, flip, showHeading }) {
  const [open, setOpen] = useState(true);
  const count = band.name && /^\d{4}$/.test(band.name) ? null : band.articles.length;
  // A group with no heading has nothing to click, so it can never be folded shut.
  const foldable = showHeading && Boolean(band.name);
  return (
    <section className="mb-[10px]">
      {showHeading && (
        <Band
          label={band.name}
          count={count}
          open={open}
          onToggle={foldable ? () => setOpen((o) => !o) : undefined}
        />
      )}
      {(!foldable || open) && <StageBand band={band} numberOf={numberOf} flip={flip} />}
    </section>
  );
}

/**
 * Back to the top, once you are far enough down to want it.
 *
 * The letters run long — several screens of a single 1791 letter — and the
 * only way back to the index was a long scroll (Sam, 2026-09-17). Hidden until
 * you are a screen and a half down, so it never sits over the opening.
 *
 * It scrolls the same element the page scrolls: the app puts its content in a
 * scrolling panel, so window.scrollTo alone would do nothing.
 */
function BackToTop() {
  const [shown, setShown] = useState(false);

  const scroller = useCallback(() => {
    let el = document.querySelector("[data-footprint-top]");
    while (el && el !== document.body) {
      const o = getComputedStyle(el).overflowY;
      if ((o === "auto" || o === "scroll") && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }
    return document.scrollingElement || document.documentElement;
  }, []);

  useEffect(() => {
    const target = scroller();
    const watched = target === document.scrollingElement || target === document.documentElement ? window : target;
    const onScroll = () => {
      const y = watched === window ? window.scrollY : target.scrollTop;
      setShown(y > window.innerHeight * 1.5);
    };
    onScroll();
    watched.addEventListener("scroll", onScroll, { passive: true });
    return () => watched.removeEventListener("scroll", onScroll);
  }, [scroller]);

  const toTop = () => {
    const target = scroller();
    const smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (target === document.scrollingElement || target === document.documentElement) {
      window.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    } else {
      target.scrollTo({ top: 0, behavior: smooth ? "smooth" : "auto" });
    }
  };

  return (
    <button
      type="button"
      onClick={toTop}
      aria-label="Back to the top"
      tabIndex={shown ? 0 : -1}
      aria-hidden={!shown}
      className={"fixed z-50 right-[20px] h-[46px] w-[46px] rounded-full " +
                 "grid place-items-center bg-[#fcfaf4] dark:bg-neutral-800 " +
                 "border border-blairGold/50 text-blairNavy dark:text-darkText " +
                 "shadow-[0_8px_20px_-8px_rgba(32,43,75,0.5)] " +
                 "hover:bg-white hover:border-blairGold focus:outline-none " +
                 "focus-visible:ring-2 focus-visible:ring-blairNavy dark:focus-visible:ring-blairGold " +
                 "transition-[opacity,transform] duration-200 motion-reduce:transition-none " +
                 (shown ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 translate-y-[8px]")}
      style={{ bottom: "calc(20px + env(safe-area-inset-bottom, 0px))" }}
    >
      <span aria-hidden="true" className="text-[17px] leading-none">↑</span>
    </button>
  );
}

// The close of the index: three small diamonds, the way a printer ends a
// section. Quiet enough not to compete with the chapter headings below it.
function IndexEnd() {
  // The delineation Sam asked for, with nothing ornamental in it. It was three
  // diamonds, then a drawn footprint — he found the gold print odd and wanted
  // the footprint only as the white nav icon (2026-09-17). A quiet navy
  // hairline does the job: the index has ended, the reading begins.
  return (
    <div className="px-[24px] pt-[30px] pb-[6px]" aria-hidden="true">
      <div className="max-w-[860px] mx-auto flex items-center justify-center">
        <span className="h-px w-full max-w-[420px] bg-gradient-to-r
                         from-transparent via-blairNavy/25 to-transparent" />
      </div>
    </div>
  );
}

function Band({ label, count, open, onToggle }) {
  if (!label) return null;
  // Centred with a rule either side, echoing the chapter break further down the
  // page. It used to be flush left with the rule trailing off to the right,
  // which fought the centred tiles beneath it.
  //
  // When a group can be folded the whole heading becomes the button, so the
  // target is the full width of the rule rather than a small chevron — kinder
  // on a phone and to anyone whose aim is not perfect.
  const inside = (
    <>
      <span className="flex-1 h-px bg-blairNavy/18" aria-hidden="true" />
      <span className="flex items-baseline gap-[9px] whitespace-nowrap">
        <span className="font-blairHead text-[12px] tracking-[0.22em] uppercase
                         text-blairNavy/85 dark:text-darkText">
          {label}
        </span>
        {count != null && (
          <span className="font-blairBody font-semibold text-[12px] tracking-[0.12em] uppercase text-blairGoldDk dark:text-blairGold">
            {count} {count === 1 ? "part" : "parts"}
          </span>
        )}
        {/* The arrowhead sits with the WORDS, not out past the rule, so it is
            obvious it belongs to this title and that the title can be folded
            (Sam, 2026-09-17). Out at the edge nobody connected the two. */}
        {onToggle && (
          // Drawn, not a text glyph: ▾ and ⌄ depend on font coverage and can
          // land as a tofu box on an older phone. A simple triangle (Sam,
          // 2026-09-17), pointing down when open and turned when folded.
          <svg aria-hidden="true" viewBox="0 0 10 7" width="9" height="7" focusable="false"
               className={"ml-[3px] shrink-0 fill-blairGoldDk dark:fill-blairGold " +
                          "transition-transform duration-200 motion-reduce:transition-none " +
                          (open ? "" : "-rotate-90")}>
            <polygon points="0,0 10,0 5,7" />
          </svg>
        )}
      </span>
      <span className="flex-1 h-px bg-blairNavy/18" aria-hidden="true" />
    </>
  );

  const box = "flex items-center gap-[14px] mt-[22px] mb-[11px] first:mt-[6px] max-w-[860px] mx-auto w-full";
  if (!onToggle) return <div className={box}>{inside}</div>;
  // FIX: py-[8px] gives the row a real height. Width was never the problem —
  // the row was already full-width and about 15px TALL, under the 24px minimum,
  // and it is the only way to re-open a group folded by accident.
  // FIX: the ring is navy on cream. blairGold on dupontCream is 1.48:1 where a
  // focus indicator needs 3:1 — the same mistake already fixed in the Scrapbook
  // earlier today, repeated here.
  return (
    <button type="button" onClick={onToggle} aria-expanded={open}
            aria-label={count != null ? `${label}, ${count} ${count === 1 ? "part" : "parts"}` : label}
            className={box + " bg-transparent border-0 py-[8px] px-0 cursor-pointer text-left " +
                       "hover:opacity-80 transition-opacity motion-reduce:transition-none " +
                       "focus:outline-none focus-visible:ring-2 focus-visible:ring-blairNavy " +
                       "dark:focus-visible:ring-blairGold rounded-[4px]"}>
      {inside}
    </button>
  );
}

// A quiet "Try again" in the magazine's own type, for a piece or a section
// that failed to load.
function RetryButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-[8px] font-blairBody font-semibold text-[14px] text-blairNavy dark:text-blairGold
                 underline underline-offset-2 bg-transparent border-0 cursor-pointer hover:opacity-80"
    >
      Try again
    </button>
  );
}

function SectionMagazine({ section }) {
  const { data: articles, isLoading, isError, isFetching, error, refetch } =
    useGetFootprintArticlesQuery(section);

  // Nothing to show yet. While "Try again" runs, RTK Query reports neither
  // loading nor error, but it keeps the last error: that is how a retry is told
  // apart from a first load (and never shown as "Nothing here yet").
  if (articles === undefined && (isLoading || isFetching)) {
    if (!error) return <p className="text-gray-500 py-[40px] text-center">Loading…</p>;
    return (
      <div className="py-[40px] text-center">
        <p className="text-gray-500 dark:text-gray-400">Couldn’t load this section right now.</p>
        <p className="text-gray-500 dark:text-gray-400 text-[14px] mt-[8px]">Trying again…</p>
      </div>
    );
  }
  if (isError)
    return (
      <div className="py-[40px] text-center">
        <p className="text-gray-500 dark:text-gray-400">Couldn’t load this section right now.</p>
        <RetryButton onClick={refetch} />
      </div>
    );
  if (!articles || articles.length === 0)
    return (
      <div className="text-center py-[60px] text-gray-500 dark:text-gray-400">
        <p className="text-[17px]">Nothing here yet.</p>
        <p className="text-[14px] mt-[6px]">This section is being prepared.</p>
      </div>
    );

  // The index keeps whatever grouping suits the section — series for Stories,
  // kind for Landmarks, year elsewhere — while the spreads below run in that
  // same order, so the numbering always matches.
  let bands;
  if (section === "attractions") {
    bands = orderPlaces(articles);
  } else {
    const { series, singles } = groupArticles(articles);
    bands = series.length > 0
      ? [...series.map((s) => ({ ...s, isSeries: true })), ...(singles.length ? [{ name: "More", articles: singles }] : [])]
      : groupByYear(singles);
  }

  const ordered = bands.flatMap((b) => b.articles);
  const numberOf = new Map(ordered.map((a, i) => [a.slug, i + 1]));

  // The first piece of each band opens a new chapter — but only when there is
  // more than one band, otherwise the divider would announce the only category
  // on the page.
  const opensBand = new Map();
  if (bands.length > 1) {
    bands.forEach((b) => {
      if (b.articles.length > 0) opensBand.set(b.articles[0].slug, b);
    });
  }

  return (
    <div className="relative" data-footprint-top>
      <ScrollCue />
      <BackToTop />
      <div className="mb-[4px] max-w-[1080px] mx-auto px-[24px]">
        {(() => {
          // Only a group that actually SHOWS a side may consume an alternation
          // slot. A pair renders two equal cards and a single is centred, so
          // neither has a side — counting them with `i % 2` left two
          // stage-left groups in a row, which is the very thing this was built
          // to fix. "Little Alexis" is a real two-part series in the live data,
          // so this bit on the actual page (audit of this PR, 2026-09-17).
          let sided = -1;
          return bands.map((b) => {
            // Every group with more than one piece shows a side now — a stage
            // on the left or right, or a pair whose lead card swaps. So every
            // one of them takes the next slot, and nothing running down the
            // page starts on the same side twice. A lone centred piece has no
            // side and is skipped.
            const alternates = b.articles.length >= 3;
            if (alternates) sided += 1;
            return (
              <IndexGroup
                key={`${section}|${b.name || "all"}`}
                band={b}
                numberOf={numberOf}
                flip={alternates && sided % 2 === 1}
                showHeading={bands.length > 1}
              />
            );
          });
        })()}
      </div>

      {/* The index has ended and the reading begins. Without this the last tile
          simply stopped and the first chapter heading arrived out of nowhere
          (Sam, 2026-09-17: "need a clear deliniation... a decorative end of
          page subtle sign"). A printer's mark, which is what this page is
          pretending to be. */}
      <IndexEnd />

      {/* The long-form reading begins here. ScrollCue watches for this to decide
          when to dissolve, so the "scroll to read" nudge survives the whole
          index instead of fading 450px in. */}
      <div className="mt-[18px]" data-footprint-reading>   {/* spreads run full width; each block constrains itself */}
        {ordered.map((a) => {
          const opens = opensBand.get(a.slug);
          return (
            <React.Fragment key={a.id}>
              {opens && (
                <CategoryBreak
                  label={opens.name}
                  count={/^\d{4}$/.test(opens.name) ? null : opens.articles.length}
                />
              )}
              <Spread article={a} n={numberOf.get(a.slug)} opensBand={Boolean(opens)} />
            </React.Fragment>
          );
        })}
      </div>

      <Contents bands={bands} numberOf={numberOf} />
    </div>
  );
}

export default SectionMagazine;
